import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';

import {
  Page,
  Hero,
  HeroImg,
  HeroOverlay,
  HeroContent,
  HeroCert,
  HeroTitle,
  HeroTitleAccent,
  HeroSub,
  HeroButtons,
  PrimaryButton,
  Wrap,
  NumWrap,
  Shop,
  ShopHeader,
  Eyebrow,
  ShopHeading,
  CatGrid,
  CatTile,
  CatTileBody,
  CatTileLink,
  BestSellersSection,
  ProductsWrapper,
  ViewAllBanner,
  ViewAllBannerTitle,
  ViewAllBannerButton,
  Trust,
  TrustItem,
  TrustDivider,
  TrustNum,
  TrustNumSmall,
  TrustLabel,
  Faq,
  FaqHeading,
  FaqItem,
  FaqQuestion,
  FaqIcon,
  FaqAnswer,
  FooterCta,
  FooterCtaText,
  FooterCtaButton,
  SubText,
} from './HalalPage.style';
import Product from '../../components/Product/Product';
import LinkifiedText from '../../components/LinkifiedText/LinkifiedText';
import {syncBasketQuantityWithProducts} from '../../helpers/product.helper';
import {setCurrentRoute} from '../../actions';
import {getHalalBestSellers} from '../../actions/halalBestSellers.action';
import {setLoginRedirect} from '../../actions/loginRedirect.action';
import {ANALYTICS_SOURCES, MESSAGES, ROUTES} from '../../constants/constants';
import {
  HALAL_ALL_URL,
  HALAL_BANNER_IMAGE_URL,
  HALAL_BEST_SELLER_ITEM_IDS,
  HALAL_CATEGORIES,
  HALAL_FAQ_ITEMS,
} from '../../constants/search.constants';
import {HALAL_PATH} from '../../constants/routePaths';
import {setPageHeadTags} from '../../helpers/seo.helper';

const HalalPage = ({
  updateBasket,
  updateItem,
  removeItem,
  onProductClick,
  deviceInfo,
}) => {
  const dispatch = useDispatch();
  const branch = useSelector(state => state.branch);
  const basketHashMap = useSelector(state => state.basketHashMap);
  const loading = useSelector(state => state.loading);
  const basket = useSelector(state => state.basket);
  const halalBestSellers = useSelector(state => state.halalBestSellers);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const toggleFaq = index =>
    setOpenFaqIndex(current => (current === index ? null : index));

  useEffect(() => {
    dispatch(setCurrentRoute(ROUTES.HALAL));
    // So that adding an item while logged out (or any other login trigger from this page)
    // returns here afterwards, instead of falling back to home.
    dispatch(setLoginRedirect(HALAL_PATH));
    setPageHeadTags({
      title: 'Halal Wholesale — Chicken, Meat & Doner at JJ Foodservice',
      description:
        'Shop the JJ Foodservice halal range — fresh and frozen chicken, lamb, beef, doner and shawarma, sausages and more, for next-day delivery or branch collection.',
    });
    dispatch(getHalalBestSellers(HALAL_BEST_SELLER_ITEM_IDS, branch));
    // Deliberately once on mount, like the category/banner content above —
    // this is a fixed showcase list, not something that should re-fetch on
    // every branch or basket change. `branch` is read once here; the saga
    // falls back to the store's own branch if this snapshot is still null.
  }, [dispatch]);

  // SearchPage renders as a sibling swap under the same scroll container, so
  // navigating there while scrolled down on this long page leaves the scroll
  // position where it was — reset it explicitly on every outbound link here.
  // The re-assert after two frames guards against anything (results reflow,
  // mobile browser focus handling) nudging the scroll back down again after
  // the initial reset, the same class of issue seen on SearchPage's pills.
  const scrollToTop = () => {
    window.scrollTo(0, 0);
    requestAnimationFrame(() =>
      requestAnimationFrame(() => window.scrollTo(0, 0))
    );
  };

  return (
    <Page>
      <Hero>
        <HeroImg src={HALAL_BANNER_IMAGE_URL} alt="Halal wholesale range" />
        <HeroOverlay />
        <HeroContent>
          <HeroCert>
            <span>حلال</span>
            <span>HALAL</span>
          </HeroCert>
          <HeroTitle>
            Halal Wholesale
            <br />
            <HeroTitleAccent>Chicken, Meat &amp; Doner</HeroTitleAccent>
          </HeroTitle>
          <HeroSub>
            Explore our halal range — delivered next day* or collect in branch.
          </HeroSub>
          <HeroButtons>
            <PrimaryButton to={HALAL_ALL_URL} onClick={scrollToTop}>
              Shop the halal range
            </PrimaryButton>
          </HeroButtons>
        </HeroContent>
      </Hero>

      <Wrap>
        <Shop>
          <ShopHeader>
            <Eyebrow>Shop by Category</Eyebrow>
            <ShopHeading>Find your cut, pack size and format</ShopHeading>
          </ShopHeader>
          <CatGrid>
            {HALAL_CATEGORIES.map(category => (
              <CatTile
                key={category.key}
                to={category.url}
                onClick={scrollToTop}
              >
                <CatTileBody>
                  <h4>{category.label}</h4>
                  <p>{category.description}</p>
                  <CatTileLink>{category.shopLabel} →</CatTileLink>
                </CatTileBody>
              </CatTile>
            ))}
          </CatGrid>
        </Shop>
      </Wrap>

      <ViewAllBanner>
        <ViewAllBannerTitle>View all halal products</ViewAllBannerTitle>
        <ViewAllBannerButton to={HALAL_ALL_URL} onClick={scrollToTop}>
          Browse the full range →
        </ViewAllBannerButton>
      </ViewAllBanner>

      <NumWrap>
        <Trust>
          <TrustItem>
            <TrustNum>
              Next Day<TrustNumSmall>*</TrustNumSmall>
            </TrustNum>
            <TrustLabel>Delivery across the UK</TrustLabel>
          </TrustItem>
          <TrustDivider />
          <TrustItem>
            <TrustNum>250+</TrustNum>
            <TrustLabel>Halal products in stock</TrustLabel>
          </TrustItem>
        </Trust>
      </NumWrap>

      {halalBestSellers && halalBestSellers.length > 0 && (
        <BestSellersSection>
          <ShopHeader>
            <Eyebrow>Best Sellers</Eyebrow>
            <ShopHeading>Halal Essentials</ShopHeading>
          </ShopHeader>
          <ProductsWrapper>
            {/* Without this, the Add button never flips to a quantity stepper once the item is
                in the basket — Product reads quantity from product.basketQuantity, which only
                search/offers/etc. pages set by running their items through this helper. */}
            {syncBasketQuantityWithProducts(
              basketHashMap,
              halalBestSellers
            ).map(product => (
              <Product
                key={product.itemId}
                product={product}
                onProductClick={onProductClick}
                updateBasket={updateBasket}
                updateItem={updateItem}
                removeItem={removeItem}
                loading={loading}
                branch={branch}
                basketHashMap={basketHashMap}
                fulfillmentType={basket && basket.fulfillmentType}
                deviceInfo={deviceInfo}
                source={ANALYTICS_SOURCES.HALAL_BEST_SELLER}
              />
            ))}
          </ProductsWrapper>
        </BestSellersSection>
      )}

      <Wrap>
        <Faq>
          <FaqHeading>Common questions</FaqHeading>
          {HALAL_FAQ_ITEMS.map((item, i) => (
            <React.Fragment key={item.question}>
              <FaqItem onClick={() => toggleFaq(i)}>
                <FaqQuestion>{item.question}</FaqQuestion>
                <FaqIcon $open={openFaqIndex === i} />
              </FaqItem>
              <FaqAnswer $open={openFaqIndex === i}>
                <LinkifiedText text={item.answer} />
              </FaqAnswer>
            </React.Fragment>
          ))}
        </Faq>
        <SubText>{MESSAGES.DELIVERY_ASSIGNED_NOTICE}</SubText>
      </Wrap>

      <FooterCta>
        <FooterCtaText>
          The full halal range for next-day* delivery.
        </FooterCtaText>
        <FooterCtaButton to={HALAL_ALL_URL} onClick={scrollToTop}>
          Shop the halal range
        </FooterCtaButton>
      </FooterCta>
    </Page>
  );
};

HalalPage.propTypes = {
  updateBasket: PropTypes.func.isRequired,
  updateItem: PropTypes.func.isRequired,
  removeItem: PropTypes.func.isRequired,
  onProductClick: PropTypes.func.isRequired,
  deviceInfo: PropTypes.object,
};

export default HalalPage;
